import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatRef } from '@promanage/core';
import { Amount, Button, Chip, Panel, PanelHeader, Ref } from '@promanage/ui';
import { LoadFailed, QueryLoading } from '../components/QueryState';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { ReportWorkspace } from '../features/reports/ReportWorkspace';
import { StatTile } from '../features/dashboard/StatTile';
import { letterheadLogo } from '../features/branding/letterhead';
import { portfolioScopeLabel } from '../features/portfolio/PortfolioScopeOptions';
import { usePortfolioScope } from '../features/portfolio/scope';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/reports/refunds')({ component: RefundsPage });

/**
 * Refunds due — what the agency is holding that belongs to a tenant.
 *
 * ── THE SHAPE OF THE PAGE IS THE POINT IT MAKES ──────────────────────────────────────────────
 *
 * Three tiles, not one. "Held" is a single ledger figure and it is NOT a single fact: rent paid
 * in advance and money left behind by a tenant who has gone are the same credit balance and
 * completely different obligations. The report leads with what can be paid today, states what is
 * merely early beside it, and flags what is stuck — so the headline cannot be read as a cheque to
 * write. Collapsing them would be the one way to make this page actively harmful.
 *
 * ── AND WHY THERE IS A BREAKDOWN WITH ONE ROW IN IT ──────────────────────────────────────────
 *
 * Overpaid rent is the only source built. Invoice credits and other funds held are the obvious
 * next ones, and both are the same question asked of a different ledger — so the server sends a
 * LIST of components and this renders whatever is in it. Adding a source changes neither this
 * file nor the table below.
 */
function RefundsPage() {
  const { scope } = usePortfolioScope();
  const report = useQuery(
    trpc.reports.refundsDue.queryOptions(
      { scope },
      { meta: { errorTitle: 'Could not work out what is owed back' }, staleTime: 30_000 },
    ),
  );
  const orgs = useQuery(trpc.auth.organisations.queryOptions(undefined, { staleTime: 60_000 }));
  const orgName = orgs.data?.find((org) => org.isCurrent)?.name ?? null;
  const data = report.data;
  const coverage = portfolioScopeLabel(scope);

  return (
    <ReportWorkspace
      title="Refunds due"
      subtitle="Money held in the trust account that belongs to a tenant"
      actions={
        <>
          <ExportPdfButton
            label="Download PDF"
            disabled={data === undefined}
            onExport={async () => {
              if (data === undefined) return;
              /*
               * Fetched by the CLICK. jspdf plus autotable is larger than the app shell, and a
               * static import here would blow the 180 KB initial budget (DESIGN_SYSTEM §8) with
               * nothing in CI failing — an oversized bundle still works.
               */
              const { exportRefundsDuePdf } = await import('../features/reports/refundsDuePdf');
              exportRefundsDuePdf({
                report: data,
                // The scope goes ON the document: two managers comparing printouts that disagree
                // is a support ticket unless each page says whose book it covers.
                coverage,
                orgName,
                logo: await letterheadLogo(),
                generatedAt: new Date(),
              });
            }}
          />
          <Button size="sm" disabled={data === undefined} onClick={() => window.print()}>
            Print
          </Button>
        </>
      }
      controls={
        <Panel>
          <PanelHeader
            title="What this covers"
            /*
             * The scope is stated, not merely applied. A worklist that quietly showed half the
             * agency's money would be read as all of it — and the control that changes it lives
             * in the account menu, which is not where somebody doubting a total looks first.
             */
            subtitle={`${coverage} · every letting holding tenant money${
              data ? ` · as at ${data.asAt}` : ''
            }`}
          />
        </Panel>
      }
    >
      {report.isError ? (
        <LoadFailed retry={() => void report.refetch()} retrying={report.isFetching} />
      ) : report.isPending ? (
        <QueryLoading label="Reading tenant balances..." />
      ) : data ? (
        <div className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <StatTile
              label="Due back"
              value={BigInt(data.summary.dueCents ?? '0')}
              detail="Tenancies that have ended, nothing in the way"
            />
            <StatTile
              label="Paid in advance"
              value={BigInt(data.summary.inAdvanceCents ?? '0')}
              detail="Still running — will be applied as rent falls due"
            />
            <StatTile
              label="Awaiting recovery"
              value={BigInt(data.summary.blockedCents ?? '0')}
              /*
               * `warn`, never `danger`. Money waiting to be recovered is a job to do, not a
               * failure — red is reserved for error and destruction (DESIGN_SYSTEM §3), and
               * teaching operators to see it here makes them blind to the alarm that matters.
               */
              tone={BigInt(data.summary.blockedCents ?? '0') > 0n ? 'warn' : 'neutral'}
              detail="Owner or agency funds must be replenished first"
            />
          </div>

          <Panel>
            <PanelHeader
              title="What it is made up of"
              subtitle="Every source of tenant money the agency holds. More will be added here."
            />
            <ul className="divide-y divide-rule px-5 pb-4">
              {data.summary.byComponent.map((component) => (
                <li key={component.kind} className="flex items-baseline justify-between gap-4 py-3">
                  <div className="flex flex-col">
                    <span className="text-[0.8125rem] font-semibold">{component.label}</span>
                    <span className="text-[0.75rem] text-ink-subtle">{component.hint}</span>
                  </div>
                  <Amount
                    value={BigInt(component.amountCents ?? '0')}
                    currency
                    notation="accounting"
                    className="text-[0.8125rem] font-semibold"
                  />
                </li>
              ))}
            </ul>
          </Panel>

          <Panel>
            <PanelHeader
              title="Tenancies holding money"
              // A total says what it covers — never a bare count beside a filtered list.
              subtitle={
                <>
                  {data.summary.count} {data.summary.count === 1 ? 'tenancy' : 'tenancies'}
                  {data.summary.totalCents === null ? null : (
                    <>
                      {' · '}
                      <Amount
                        value={BigInt(data.summary.totalCents)}
                        currency
                        tone="neutral"
                      />{' '}
                      held in total
                    </>
                  )}
                </>
              }
            />
            {data.rows.length === 0 ? (
              <p className="px-5 pb-5 text-[0.8125rem] text-ink-muted">
                No tenant is holding a credit balance. Nothing to refund.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[46rem] border-collapse text-[0.8125rem]">
                  <caption className="px-5 pb-2 text-left text-[0.6875rem] text-ink-subtle">
                    Money the tenant has paid that has not been applied to a rent charge. A tenancy
                    that OWES rent is not here — that is the arrears register.
                  </caption>
                  <thead>
                    <tr className="border-t border-rule text-left text-[0.6875rem] tracking-wide text-ink-subtle uppercase">
                      <th className="px-5 py-2 font-semibold">Property</th>
                      <th className="px-5 py-2 font-semibold">Tenants</th>
                      <th className="px-5 py-2 font-semibold">Standing</th>
                      <th className="px-5 py-2 text-right font-semibold">Held</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row) => (
                      <tr key={row.tenancyId} className="border-t border-rule align-top">
                        <td className="px-5 py-3">
                          <Link
                            to="/tenancies/$ref"
                            params={{ ref: formatRef('tenancy', row.tenancyRefSeq) }}
                            className="font-semibold text-accent hover:underline"
                          >
                            {row.address}
                          </Link>
                          <div className="pt-0.5">
                            <Ref entity="tenancy" sequence={row.tenancyRefSeq} />
                          </div>
                        </td>
                        <td className="px-5 py-3">{row.tenants || '—'}</td>
                        <td className="px-5 py-3">
                          {/* Never colour alone: the chip carries the word, and the date under
                              it says how long the money has been sitting there. */}
                          <Chip tone={standingTone(row.standing)}>{row.standingLabel}</Chip>
                          {row.endsOn ? (
                            <div className="pt-1 text-[0.75rem] text-ink-subtle">
                              Ended {row.endsOn}
                            </div>
                          ) : null}
                          {BigInt(row.recoveryCents) > 0n ? (
                            <div className="pt-1 text-[0.75rem] text-warn">
                              <Amount value={BigInt(row.recoveryCents)} currency tone="warn" /> to
                              recover
                            </div>
                          ) : null}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <Amount
                            value={BigInt(row.totalCents)}
                            currency
                            notation="accounting"
                            className="font-semibold"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      ) : null}
    </ReportWorkspace>
  );
}

/** `warn` for anything blocked; everything else stays neutral. Red is not a standing. */
function standingTone(standing: string): 'neutral' | 'accent' | 'warn' {
  if (standing === 'blocked') return 'warn';
  return standing === 'due' ? 'accent' : 'neutral';
}
